/**
 * Central Firestore Operation & Quota Tracker
 *
 * Tracks every Read, Write, and Delete operation performed in the application
 * and maintains daily quotas (50k Reads / 20k Writes / 20k Deletes) with
 * collection breakdowns and 7-day historical trends.
 */

import { safeLocalStorageGet, safeLocalStorageSet } from './storageHelper'

export interface CollectionOpDetail {
  reads: number
  writes: number
  deletes: number
  totalOps: number
}

export interface DailyMetrics {
  date: string // YYYY-MM-DD
  reads: number
  writes: number
  deletes: number
  totalOps: number
  maxDailyReads: number
  maxDailyWrites: number
  maxDailyDeletes: number
  readsPercentage: number
  writesPercentage: number
  deletesPercentage: number
  readsRemaining: number
  writesRemaining: number
  deletesRemaining: number
  breakdownByCollection: Record<string, CollectionOpDetail>
  hourlyDistribution: Record<string, { reads: number; writes: number; deletes: number }>
}

export const SPARK_QUOTA_LIMITS = {
  MAX_DAILY_READS: 50000,
  MAX_DAILY_WRITES: 20000,
  MAX_DAILY_DELETES: 20000,
  MAX_STORAGE_BYTES: 1024 * 1024 * 1024 // 1 GB
} as const

const STORAGE_PREFIX = 'kochbuch_firestore_ops_'

function getTodayString(): string {
  return new Date().toISOString().slice(0, 10)
}

function getHourString(): string {
  return String(new Date().getHours()).padStart(2, '0')
}

interface RawStoredMetrics {
  date: string
  reads: number
  writes: number
  deletes: number
  breakdown: Record<string, { reads: number; writes: number; deletes: number }>
  hourly: Record<string, { reads: number; writes: number; deletes: number }>
}

function getRawMetrics(dateStr: string = getTodayString()): RawStoredMetrics {
  const key = `${STORAGE_PREFIX}${dateStr}`
  return safeLocalStorageGet<RawStoredMetrics>(key, {
    date: dateStr,
    reads: 0,
    writes: 0,
    deletes: 0,
    breakdown: {},
    hourly: {}
  })
}

function saveRawMetrics(metrics: RawStoredMetrics): void {
  const key = `${STORAGE_PREFIX}${metrics.date}`
  safeLocalStorageSet(key, metrics)
}

/**
 * Tracks document read operation(s).
 */
export function trackRead(collectionName = 'general', count = 1): void {
  if (count <= 0) return
  const raw = getRawMetrics()
  const hour = getHourString()

  raw.reads = (raw.reads || 0) + count

  // Collection breakdown
  if (!raw.breakdown[collectionName]) {
    raw.breakdown[collectionName] = { reads: 0, writes: 0, deletes: 0 }
  }
  raw.breakdown[collectionName].reads = (raw.breakdown[collectionName].reads || 0) + count

  // Hourly distribution
  if (!raw.hourly[hour]) {
    raw.hourly[hour] = { reads: 0, writes: 0, deletes: 0 }
  }
  raw.hourly[hour].reads = (raw.hourly[hour].reads || 0) + count

  saveRawMetrics(raw)
}

/**
 * Tracks document write/set/update operation(s).
 */
export function trackWrite(collectionName = 'general', count = 1): void {
  if (count <= 0) return
  const raw = getRawMetrics()
  const hour = getHourString()

  raw.writes = (raw.writes || 0) + count

  if (!raw.breakdown[collectionName]) {
    raw.breakdown[collectionName] = { reads: 0, writes: 0, deletes: 0 }
  }
  raw.breakdown[collectionName].writes = (raw.breakdown[collectionName].writes || 0) + count

  if (!raw.hourly[hour]) {
    raw.hourly[hour] = { reads: 0, writes: 0, deletes: 0 }
  }
  raw.hourly[hour].writes = (raw.hourly[hour].writes || 0) + count

  saveRawMetrics(raw)
}

/**
 * Tracks document delete operation(s).
 */
export function trackDelete(collectionName = 'general', count = 1): void {
  if (count <= 0) return
  const raw = getRawMetrics()
  const hour = getHourString()

  raw.deletes = (raw.deletes || 0) + count

  if (!raw.breakdown[collectionName]) {
    raw.breakdown[collectionName] = { reads: 0, writes: 0, deletes: 0 }
  }
  raw.breakdown[collectionName].deletes = (raw.breakdown[collectionName].deletes || 0) + count

  if (!raw.hourly[hour]) {
    raw.hourly[hour] = { reads: 0, writes: 0, deletes: 0 }
  }
  raw.hourly[hour].deletes = (raw.hourly[hour].deletes || 0) + count

  saveRawMetrics(raw)
}

/**
 * Returns formatted daily metrics for a specific date (defaults to today).
 */
export function getDailyMetrics(dateStr: string = getTodayString()): DailyMetrics {
  const raw = getRawMetrics(dateStr)

  const reads = raw.reads || 0
  const writes = raw.writes || 0
  const deletes = raw.deletes || 0
  const totalOps = reads + writes + deletes

  const maxReads = SPARK_QUOTA_LIMITS.MAX_DAILY_READS
  const maxWrites = SPARK_QUOTA_LIMITS.MAX_DAILY_WRITES
  const maxDeletes = SPARK_QUOTA_LIMITS.MAX_DAILY_DELETES

  const breakdownByCollection: Record<string, CollectionOpDetail> = {}
  for (const [col, val] of Object.entries(raw.breakdown || {})) {
    const cReads = val.reads || 0
    const cWrites = val.writes || 0
    const cDeletes = val.deletes || 0
    breakdownByCollection[col] = {
      reads: cReads,
      writes: cWrites,
      deletes: cDeletes,
      totalOps: cReads + cWrites + cDeletes
    }
  }

  return {
    date: dateStr,
    reads,
    writes,
    deletes,
    totalOps,
    maxDailyReads: maxReads,
    maxDailyWrites: maxWrites,
    maxDailyDeletes: maxDeletes,
    readsPercentage: parseFloat(((reads / maxReads) * 100).toFixed(2)),
    writesPercentage: parseFloat(((writes / maxWrites) * 100).toFixed(2)),
    deletesPercentage: parseFloat(((deletes / maxDeletes) * 100).toFixed(2)),
    readsRemaining: Math.max(0, maxReads - reads),
    writesRemaining: Math.max(0, maxWrites - writes),
    deletesRemaining: Math.max(0, maxDeletes - deletes),
    breakdownByCollection,
    hourlyDistribution: raw.hourly || {}
  }
}

/**
 * Returns the last N days of historical metrics (including today).
 */
export function getHistoricalMetrics(days = 7): DailyMetrics[] {
  const results: DailyMetrics[] = []
  const now = new Date()

  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now)
    d.setDate(d.getDate() - i)
    const dateStr = d.toISOString().slice(0, 10)
    results.push(getDailyMetrics(dateStr))
  }

  return results
}

/**
 * Resets daily metrics for calibration or testing.
 */
export function resetDailyMetrics(dateStr: string = getTodayString()): void {
  const key = `${STORAGE_PREFIX}${dateStr}`
  localStorage.removeItem(key)
}
